import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import { supabase } from '../supabaseClient';
import asterisk from '../asserts/as.png';
import './Bookingpage.css';

const BookingPage = () => {
    const navigate = useNavigate();

    const headerRef = useRef(null);
    const leftRef = useRef(null);
    const formCardRef = useRef(null);
    const asteriskRef = useRef(null);
    const submitRef = useRef(null);

    const [ticketTiers, setTicketTiers] = useState([]);
    const [exhibitors, setExhibitors] = useState([]);
    const [policies, setPolicies] = useState([]);

    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');
    const [selectedExhibitor, setSelectedExhibitor] = useState('');
    const [ticketType, setTicketType] = useState('');
    const [quantity, setQuantity] = useState(1);
    const [submitStatus, setSubmitStatus] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const selectedTier = ticketTiers.find(t => t.tier_name_en === ticketType);
    const unitPrice = selectedTier?.price_amount ?? 0;
    const totalPrice = unitPrice * quantity;

    useEffect(() => {
        const fetchAll = async () => {
            const [tiersRes, exhibitorsRes, policiesRes] = await Promise.all([
                supabase.from('ticket_tiers').select('*').eq('is_active', true).order('display_order'),
                supabase.from('exhibitors').select('id, name'),
                supabase.from('booking_policies').select('*').order('display_order'),
            ]);
            setTicketTiers(tiersRes.data || []);
            setExhibitors(exhibitorsRes.data || []);
            setPolicies(policiesRes.data || []);
            if (tiersRes.data?.length > 0) setTicketType(tiersRes.data[0].tier_name_en);
        };
        fetchAll();
    }, []);

    useEffect(() => {
        const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

        gsap.set(headerRef.current, { opacity: 0, y: -20 });
        gsap.set(leftRef.current, { opacity: 0, x: -40 });
        gsap.set(formCardRef.current, { opacity: 0, y: 60, scale: 0.92 });
        gsap.set(asteriskRef.current, { opacity: 0, rotation: -180, scale: 0 });

        tl.to(headerRef.current, { opacity: 1, y: 0, duration: 0.6 })
          .to(leftRef.current, { opacity: 1, x: 0, duration: 0.7 }, '-=0.3')
          .to(formCardRef.current, { opacity: 1, y: 0, scale: 1, duration: 0.8, ease: 'expo.out' }, '-=0.4')
          .to(asteriskRef.current, { opacity: 1, rotation: 0, scale: 1, duration: 0.8, ease: 'elastic.out(1,0.5)' }, '-=0.5');

        gsap.to(asteriskRef.current, { rotation: 360, duration: 10, repeat: -1, ease: 'none', delay: 1.5 });

        gsap.fromTo('.bk-field', { opacity: 0, y: 16, filter: 'blur(4px)' },
            { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.45, stagger: 0.1, ease: 'power2.out', delay: 0.9 });

        gsap.fromTo('.bk-tier-btn', { opacity: 0, x: 30 },
            { opacity: 1, x: 0, duration: 0.45, stagger: 0.1, ease: 'back.out(1.5)', delay: 1.2 });
    }, []);

    const handleQuantityChange = (delta) =>
        setQuantity(prev => Math.max(1, Math.min(10, prev + delta)));

    const handleConfirm = async (e) => {
        if (e) e.preventDefault();
        if (!fullName.trim() || !email.trim() || !selectedTier) {
            setErrorMessage('Please fill in your name, email and select a ticket type.');
            setSubmitStatus('error');
            return;
        }
        setSubmitStatus('loading');
        setErrorMessage('');

        const { data: userData, error: userError } = await supabase
            .from('users')
            .insert([{ full_name: fullName.trim(), email: email.trim(), role: 'User', status: 'UNVERIFIED' }])
            .select('id')
            .single();

        if (userError) {
            setErrorMessage('User error: ' + userError.message);
            setSubmitStatus('error');
            return;
        }

        const { error: bookingError } = await supabase
            .from('booking')
            .insert([{
                user_name: fullName.trim(),
                total_paid: totalPrice,
                payment_status: 'PENDING',
                user_id: userData.id,
                ticket_tier_id: selectedTier.id,
                quantity,
                exhibitor_id: selectedExhibitor || null,
            }]);

        if (bookingError) {
            setErrorMessage('Booking error: ' + bookingError.message);
            setSubmitStatus('error');
            return;
        }

        setSubmitStatus('success');
        gsap.fromTo(submitRef.current, { scale: 0.8, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.6, ease: 'back.out(1.5)' });

        const ticketId = `TDS-${selectedTier?.id?.slice(0,1) || '1'}-${Math.random().toString(36).slice(2,10).toUpperCase()}`;
        setTimeout(() => {
            navigate('/ticket-view', {
                state: {
                    fullName: fullName.trim(),
                    tierName: selectedTier?.tier_name_en,
                    totalPaid: totalPrice,
                    currency: selectedTier?.currency,
                    ticketId,
                }
            });
        }, 1200);
    };

    if (submitStatus === 'success') {
        return (
            <div className="home-wrapper">
                <div className="mobile-container">
                    <main className="bk-success-screen" role="status" aria-live="polite">
                        <div className="bk-success-icon-wrap">
                            <svg aria-hidden="true" width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                                <polyline points="22 4 12 14.01 9 11.01"/>
                            </svg>
                        </div>
                        <h2 className="bk-success-title">Booking Confirmed!</h2>
                        <p className="bk-success-desc">
                            Your <strong>{selectedTier?.tier_name_en}</strong> ticket has been submitted. You will receive a confirmation email shortly.
                        </p>
                        <div className="bk-success-badge">
                            {totalPrice.toLocaleString()} {selectedTier?.currency} — PENDING
                        </div>
                        <button className="bk-done-btn" onClick={() => navigate(-1)} aria-label="Go back to the events page">
                            BACK TO EVENT
                        </button>
                    </main>
                </div>
            </div>
        );
    }

    return (
        <div className="home-wrapper">
            <div className="mobile-container">
                <div className="bk-screen">

                    <header className="bk-header" ref={headerRef}>
                        <button className="bk-back-btn" onClick={() => navigate(-1)} aria-label="Go back to the previous page">
                            <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M19 12H5M12 5l-7 7 7 7"/>
                            </svg>
                        </button>
                        <div>
                            <h1 className="bk-header-title">BOOK YOUR TICKET</h1>
                        </div>
                    </header>

                    <main className="bk-body">

                        <section className="bk-left" ref={leftRef} aria-labelledby="booking-info-title">
                            <h2 className="bk-left-title" id="booking-info-title">BOOK YOUR TICKET</h2>
                            <p className="bk-left-text">
                                By booking your ticket, you agree to our terms and conditions regarding event access, data usage, and privacy protection. We are committed to ensuring that all personal information collected during the booking process is handled securely and in accordance with applicable data protection regulations. Your details will only be used for event-related communication, ticket verification, and improving your overall experience.
                            </p>
                            {policies.length > 0 && (
                                <div className="bk-policies">
                                    <p className="bk-policies-title">POLICY HIGHLIGHTS</p>
                                    <ul className="bk-policies-list">
                                        {policies.map(p => (
                                            <li key={p.id} className="bk-policy-item">
                                                <span className="bk-policy-sq" aria-hidden="true"></span>
                                                {p.point_text_en}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </section>

                        <section className="bk-form-card" ref={formCardRef} aria-labelledby="booking-form-title">
                            <div className="bk-form-arch" aria-hidden="true">
                                <div className="bk-asterisk-wrap">
                                    <img src={asterisk} alt="" className="bk-asterisk" ref={asteriskRef} />
                                </div>
                            </div>

                            <form className="bk-form-inner" onSubmit={handleConfirm}>
                                <h2 className="sr-only" id="booking-form-title">Ticket Booking Form</h2>
                                <div className="bk-field">
                                    <label className="bk-label" htmlFor="full-name">
                                        <svg aria-hidden="true" width="14" height="14" viewBox="0 0 16 16" fill="none">
                                            <path d="M8 8C9.933 8 11.5 6.433 11.5 4.5S9.933 1 8 1 4.5 2.567 4.5 4.5 6.067 8 8 8z" stroke="#FF1E1E" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M2 14.5c0-3.038 2.686-5.5 6-5.5s6 2.462 6 5.5" stroke="#FF1E1E" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg> FULL NAME
                                    </label>
                                    <input
                                        id="full-name"
                                        className="bk-input"
                                        type="text"
                                        placeholder="Enter your full name"
                                        value={fullName}
                                        onChange={e => setFullName(e.target.value)}
                                        required
                                        aria-required="true"
                                        aria-invalid={submitStatus === 'error' && !fullName.trim() ? "true" : "false"}
                                        aria-describedby={submitStatus === 'error' && !fullName.trim() ? "name-error" : undefined}
                                    />
                                </div>

                                <div className="bk-field">
                                    <label className="bk-label" htmlFor="email-address">
                                        <svg aria-hidden="true" width="14" height="14" viewBox="0 0 16 16" fill="none">
                                            <rect x="1" y="3" width="14" height="10" rx="1.5" stroke="#FF1E1E" strokeWidth="1.2"/>
                                            <path d="M1.5 3.5l6.5 5 6.5-5" stroke="#FF1E1E" strokeWidth="1.2" strokeLinecap="round"/>
                                        </svg> EMAIL ADDRESS
                                    </label>
                                    <input
                                        id="email-address"
                                        className="bk-input"
                                        type="email"
                                        placeholder="your-email@example.com"
                                        value={email}
                                        onChange={e => setEmail(e.target.value)}
                                        required
                                        aria-required="true"
                                        aria-invalid={submitStatus === 'error' && !email.trim() ? "true" : "false"}
                                        aria-describedby={submitStatus === 'error' && !email.trim() ? "email-error" : undefined}
                                    />
                                </div>

                                <div className="bk-field">
                                    <label className="bk-label" htmlFor="exhibitor-select">
                                        <svg aria-hidden="true" width="14" height="14" viewBox="0 0 16 16" fill="none">
                                            <rect x="1" y="2" width="14" height="12" rx="1.5" stroke="#FF1E1E" strokeWidth="1.2"/>
                                            <path d="M4 6h8M4 9.5h5" stroke="#FF1E1E" strokeWidth="1.2" strokeLinecap="round"/>
                                        </svg> EXHIBITOR
                                    </label>
                                    <div className="bk-select-wrap">
                                        <select
                                            id="exhibitor-select"
                                            className="bk-select"
                                            value={selectedExhibitor}
                                            onChange={e => setSelectedExhibitor(e.target.value)}
                                        >
                                            <option value="">Select an exhibitor</option>
                                            {exhibitors.map(ex => (
                                                <option key={ex.id} value={ex.id}>{ex.name}</option>
                                            ))}
                                        </select>
                                        <svg className="bk-select-arrow" viewBox="0 0 12 8" fill="none" aria-hidden="true">
                                            <path d="M1 1l5 5 5-5" stroke="#FF1E1E" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </div>
                                </div>

                                <fieldset className="bk-field">
                                    <legend className="bk-ticket-label">TICKET TYPE</legend>
                                    <div className="bk-tiers" role="radiogroup" aria-label="Select Ticket Tier">
                                        {ticketTiers.map(tier => (
                                            <button
                                                type="button"
                                                key={tier.id}
                                                className={`bk-tier-btn${ticketType === tier.tier_name_en ? ' bk-tier-btn--active' : ''}`}
                                                onClick={() => { setTicketType(tier.tier_name_en); setQuantity(1); }}
                                                role="radio"
                                                aria-checked={ticketType === tier.tier_name_en}
                                                aria-label={`${tier.tier_name_en} ticket, price ${tier.price_amount} ${tier.currency}`}
                                            >
                                                <span>{tier.tier_name_en}</span>
                                                <span className="bk-tier-price">{tier.price_amount.toLocaleString()} {tier.currency}</span>
                                            </button>
                                        ))}
                                    </div>
                                </fieldset>

                                {selectedTier && (
                                    <div className="bk-field bk-qty-field">
                                        <div className="bk-qty-row">
                                            <label className="bk-label" id="qty-label">
                                                <svg aria-hidden="true" width="14" height="14" viewBox="0 0 16 16" fill="none">
                                                    <rect x="1" y="3" width="14" height="11" rx="1.5" stroke="#FF1E1E" strokeWidth="1.2"/>
                                                    <path d="M5 3V2a3 3 0 016 0v1" stroke="#FF1E1E" strokeWidth="1.2" strokeLinecap="round"/>
                                                    <circle cx="8" cy="9" r="1.5" stroke="#FF1E1E" strokeWidth="1.2"/>
                                                </svg> QUANTITY
                                            </label>
                                            <div className="bk-qty-controls" role="group" aria-labelledby="qty-label">
                                                <button
                                                    type="button"
                                                    className="bk-qty-btn"
                                                    onClick={() => handleQuantityChange(-1)}
                                                    disabled={quantity <= 1}
                                                    aria-label="Decrease quantity"
                                                >
                                                    <svg viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M2 6h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                                                </button>
                                                <span className="bk-qty-val" aria-live="polite" aria-atomic="true">{quantity}</span>
                                                <button
                                                    type="button"
                                                    className="bk-qty-btn"
                                                    onClick={() => handleQuantityChange(1)}
                                                    disabled={quantity >= 10}
                                                    aria-label="Increase quantity"
                                                >
                                                    <svg viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M6 2v8M2 6h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                                                </button>
                                            </div>
                                        </div>
                                        <div className="bk-total-row">
                                            <span className="bk-total-label">TOTAL</span>
                                            <span className="bk-total-val">
                                                <span className="sr-only">Total price: </span>
                                                {totalPrice.toLocaleString()} <span className="bk-total-currency" aria-hidden="true">{selectedTier.currency}</span>
                                                <span className="sr-only">{selectedTier.currency}</span>
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {submitStatus === 'error' && (
                                    <p className="bk-error-msg" id="booking-error" role="alert">{errorMessage}</p>
                                )}

                                <div ref={submitRef}>
                                    <button
                                        type="submit"
                                        className="bk-confirm-btn"
                                        disabled={submitStatus === 'loading'}
                                        aria-busy={submitStatus === 'loading'}
                                    >
                                        {submitStatus === 'loading' ? 'CONFIRMING...' : 'CONFIRM BOOKING'}
                                    </button>
                                </div>

                                <p className="bk-note">You will receive a confirmation email with your ticket details</p>
                            </form>
                        </section>

                    </main>
                </div>
            </div>
        </div>
    );
};

export default BookingPage;